import "./App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import { AuthProvider } from "./context/AuthContext";

// Lazy-loaded pages — each ships its own chunk (heavy libs like leaflet,
// recharts & embla stay out of the homepage bundle).
const MapPage = lazy(() => import("./pages/MapPage"));
const Report = lazy(() => import("./pages/Report"));
const Lost = lazy(() => import("./pages/Lost"));
const Carers = lazy(() => import("./pages/Carers"));
const Rewards = lazy(() => import("./pages/Rewards"));
const Sitters = lazy(() => import("./pages/Sitters"));
const Hotspots = lazy(() => import("./pages/Hotspots"));
const Memorials = lazy(() => import("./pages/Memorials"));
const Breeds = lazy(() => import("./pages/Breeds"));
const Diets = lazy(() => import("./pages/Diets"));
const Volunteer = lazy(() => import("./pages/Volunteer"));
const About = lazy(() => import("./pages/About"));
const Spot = lazy(() => import("./pages/Spot"));
const BreedScanPage = lazy(() => import("./pages/BreedScanPage"));
const Favourites = lazy(() => import("./pages/Favourites"));
const Pounds = lazy(() => import("./pages/Pounds"));
const Press = lazy(() => import("./pages/Press"));
const Resources = lazy(() => import("./pages/Resources"));
const Admin = lazy(() => import("./pages/Admin"));
const AnimalDetail = lazy(() => import("./pages/AnimalDetail"));
const Adopt = lazy(() => import("./pages/Adopt"));
const Donate = lazy(() => import("./pages/Donate"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentSuccessStripe = lazy(() => import("./pages/PaymentStatus"));
const PaymentCancel = lazy(() => import("./pages/PaymentStatus").then((m) => ({ default: m.PaymentCancel })));
const Community = lazy(() => import("./pages/Community"));
const Fundraisers = lazy(() => import("./pages/Fundraisers"));
const DogParks = lazy(() => import("./pages/DogParks"));
const Auth = lazy(() => import("./pages/Auth"));
const WalkForRewards = lazy(() => import("./pages/WalkForRewards"));
const Partners = lazy(() => import("./pages/Partners"));
const MySubmissions = lazy(() => import("./pages/MySubmissions"));
const MicrochipLookup = lazy(() => import("./pages/MicrochipLookup"));
const Groomers = lazy(() => import("./pages/Groomers"));
const DogWashes = lazy(() => import("./pages/DogWashes"));
const ListBusiness = lazy(() => import("./pages/ListBusiness"));
const Calendar = lazy(() => import("./pages/Calendar"));
const RescuesPage = lazy(() => import("./pages/RescuesPage"));

function PageFallback() {
  return (
    <div className="flex items-center justify-center py-24" data-testid="page-loading">
      <Loader2 className="w-8 h-8 animate-spin text-[var(--paw-sky)]" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Layout>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/report" element={<Report />} />
                <Route path="/lost" element={<Lost />} />
                <Route path="/carers" element={<Carers />} />
                <Route path="/rewards" element={<Rewards />} />
                <Route path="/sitters" element={<Sitters />} />
                <Route path="/hotspots" element={<Hotspots />} />
                <Route path="/memorials" element={<Memorials />} />
                <Route path="/breeds" element={<Breeds />} />
                <Route path="/diets" element={<Diets />} />
                <Route path="/volunteer" element={<Volunteer />} />
                <Route path="/about" element={<About />} />
                <Route path="/spot" element={<Spot />} />
                <Route path="/breed-scan" element={<BreedScanPage />} />
                <Route path="/favourites" element={<Favourites />} />
                <Route path="/pounds" element={<Pounds />} />
                <Route path="/press" element={<Press />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/animal/:id" element={<AnimalDetail />} />
                <Route path="/adopt/:id" element={<Adopt />} />
                <Route path="/donate/:animalId" element={<Donate />} />
                <Route path="/donate" element={<Navigate to="/donate/general" replace />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment/success" element={<PaymentSuccessStripe />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/community" element={<Community />} />
                <Route path="/fundraisers" element={<Fundraisers />} />
                <Route path="/dog-parks" element={<DogParks />} />
                <Route path="/walk" element={<WalkForRewards />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/my" element={<MySubmissions />} />
                <Route path="/microchip" element={<MicrochipLookup />} />
                <Route path="/groomers" element={<Groomers />} />
                <Route path="/dog-washes" element={<DogWashes />} />
                <Route path="/list-business" element={<ListBusiness />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/rescues" element={<RescuesPage />} />
                <Route path="/auth" element={<Auth />} />
              </Routes>
            </Suspense>
          </Layout>
        </AuthProvider>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
